import { addStyles, DefaultTheme, rgba } from '../../../components/theme';
import {
	BLACK,
	ColorPaletteBlue,
	ColorPaletteGreen,
	ColorPaletteRed,
	ColorPaletteYellow,
	WHITE,
} from '../../../components/theme.palette';
import { DRAGON_ID, DRAGON_TYPE, DragonDomTypes } from '../../../dom';
import { T, Translate } from '../../../i18n';
import { m } from '../../../mithril';
import { classNames } from '../../../util/classnames';
import { CollectionModel } from '@ikegps/core-shared';

export type PoleAccuracy = 'rtk_fixed' | 'edited' | 'manual' | 'rtk_float' | 'low' | 'default';

interface ITopologicalEditorMapPointFactoryOptions {
	collection: CollectionModel;
	point: google.maps.Point;
	isCollectionSelected: boolean;
	onClick?: () => void;
	onCollectionMouseOver: (collectionId: string) => void;
	onCollectionMouseOut: () => void;
	showCollectionTitle: boolean;
	showTitlesOnMap: boolean;
	locationState?: string;
	poleAccuracy: PoleAccuracy;
}

const CIRCLE_RADIUS = DefaultTheme.spacingFactor * 1.5;
const CIRCLE_SIZE_PX = CIRCLE_RADIUS * 2 + 'px';
const CIRCLE_BORDER_SIZE = 2;
const CIRCLE_DOT_SIZE = 3;
const MAX_TITLE_WIDTH = 120;

export const TopologicalEditorPointCSS = {
	container: 'c-TopologicalEditorMapPoint',
	titleContainer: 'c-TopologicalEditorMapPoint__titleContainer',
	title: 'c-TopologicalEditorMapPoint__title',
	isSelected: 'c-TopologicalEditorMapPoint--isSelected',

	locationFixed: 'c-TopologicalEditorMapPoint--fixed',
	locationEdited: 'c-TopologicalEditorMapPoint--edited',
	locationManualOrFloat: 'c-TopologicalEditorMapPoint--manualOrFloat',
	locationLow: 'c-TopologicalEditorMapPoint--low',
	locationDefault: 'c-TopologicalEditorMapPoint--default',
};

const PointStyles = {
	[TopologicalEditorPointCSS.titleContainer]: {
		position: 'absolute',
		width: `${MAX_TITLE_WIDTH}px`,
		'justify-content': 'center',
	},
	[TopologicalEditorPointCSS.title]: {
		background: `${rgba(ColorPaletteBlue.C900, 0.5)}`,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		color: `${rgba(WHITE)}`,
		padding: '4px',
		'z-index': '500',
	},
	[TopologicalEditorPointCSS.container]: {
		height: CIRCLE_SIZE_PX,
		width: CIRCLE_SIZE_PX,
		minWidth: CIRCLE_SIZE_PX,
		maxWidth: CIRCLE_SIZE_PX,
		borderRadius: '50%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		position: 'absolute',
		background: DefaultTheme.color.accent,
		border: `${CIRCLE_BORDER_SIZE}px solid ${rgba(BLACK, 0.5)}`,
		cursor: 'pointer',
		transition: ['box-shadow'],
		transitionDuration: 150,
	},
	[`${TopologicalEditorPointCSS.container}.${TopologicalEditorPointCSS.isSelected}`]: {
		boxShadow: `0 0 0 3px rgba(${ColorPaletteBlue.C700}, 0.24), 0 0 0 3px rgba(${ColorPaletteBlue.C700}, 0.64)`,
		border: `${CIRCLE_BORDER_SIZE}px solid rgba(255, 255, 255, 0.87)`,
	},
	[`${TopologicalEditorPointCSS.container}:hover, ${TopologicalEditorPointCSS.container}.${TopologicalEditorPointCSS.isSelected}:hover`]:
		{
			boxShadow: DefaultTheme.shadow.box.dp2w5,
		},
	[TopologicalEditorPointCSS.locationFixed]: {
		backgroundColor: rgba(ColorPaletteGreen.A700),
	},
	[`${TopologicalEditorPointCSS.locationEdited}`]: {
		backgroundColor: rgba(ColorPaletteGreen.A700),

		['&::after']: {
			content: '""',
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)',
			width: `${CIRCLE_DOT_SIZE}px`,
			height: `${CIRCLE_DOT_SIZE}px`,
			borderRadius: '50%',
			backgroundColor: rgba(BLACK),
		},
	},
	[TopologicalEditorPointCSS.locationManualOrFloat]: {
		backgroundColor: rgba(ColorPaletteYellow.C400),
	},
	[TopologicalEditorPointCSS.locationLow]: {
		backgroundColor: rgba(ColorPaletteRed.A700),
	},
	[TopologicalEditorPointCSS.locationDefault]: {
		backgroundColor: DefaultTheme.color.accent,
	},
};

addStyles(PointStyles);

export const poleMeasurementAccuracyTypes: { [key in PoleAccuracy]: string } = {
	rtk_fixed: TopologicalEditorPointCSS.locationFixed,
	edited: TopologicalEditorPointCSS.locationEdited,
	manual: TopologicalEditorPointCSS.locationManualOrFloat,
	rtk_float: TopologicalEditorPointCSS.locationManualOrFloat,
	low: TopologicalEditorPointCSS.locationLow,
	default: TopologicalEditorPointCSS.locationDefault,
};

export const MapPoint = {
	view({
		collection,
		point,
		isCollectionSelected,
		poleAccuracy,
		onClick,
		onCollectionMouseOver,
		onCollectionMouseOut,
		showCollectionTitle,
		showTitlesOnMap,
	}: ITopologicalEditorMapPointFactoryOptions) {
		return [
			m('div', {
				key: collection.id,
				[DRAGON_ID]: collection.revision,
				[DRAGON_TYPE]: DragonDomTypes.COLLECTION,
				className: classNames(TopologicalEditorPointCSS.container, poleMeasurementAccuracyTypes[poleAccuracy], {
					[TopologicalEditorPointCSS.isSelected]: isCollectionSelected,
				}),
				onclick: onClick,
				style: {
					top: point.y - CIRCLE_RADIUS - CIRCLE_BORDER_SIZE + 'px',
					left: point.x - CIRCLE_RADIUS - CIRCLE_BORDER_SIZE + 'px',
				},
				onmouseover: onCollectionMouseOver,
				onmouseout: onCollectionMouseOut,
			}),
			m(
				'div',
				{
					className: TopologicalEditorPointCSS.titleContainer,
					style: {
						top: point.y - DefaultTheme.spacingFactor * 9 + 'px',
						left: point.x - MAX_TITLE_WIDTH / 2 + 'px',
						display: showCollectionTitle || showTitlesOnMap ? 'flex' : 'none',
					},
				},
				m(
					'div',
					{
						className: TopologicalEditorPointCSS.title,
					},
					collection.title || collection.subtitle || Translate(T.COLLECTION_MISSING_TITLE)
				)
			),
		];
	},
};
